import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  X, ClipboardCheck, Wrench, CreditCard, PenLine, CheckCircle2, Loader2,
  Plus, Minus, Search, Trash2, DollarSign, FileText, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { flowsApi, pricebookApi, jobsApi, paymentProvidersApi, pipelineApi } from "../../lib/api";
import JobCompletionFlow from "../JobCompletionFlow";
import SignaturePad from "../SignaturePad";
import ProcessPaymentModal from "../ProcessPaymentModal";

const STEPS = [
  { key: "jobsummary", label: "Job Summary" },
  { key: "checklist", label: "Checklist" },
  { key: "parts", label: "Parts" },
  { key: "payment", label: "Payment" },
  { key: "signature", label: "Sign-off" },
  { key: "disposition", label: "Disposition" },
  { key: "summary", label: "Done" },
];

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Dedicated mobile Work Order Completion screen:
 * Checklist → Parts installed → Collect payment → Customer signature → Close Job.
 * Parts + signature are recorded on the job (office review); payment hits the invoice live.
 */
export default function WorkOrderComplete({ job, invoices, onClose, onCompleted, desktop = false }) {
  const [step, setStep] = useState("jobsummary");
  const [checklistState, setChecklistState] = useState("none"); // none | pending | done
  // job summary — required first step, saved immediately on Continue
  const [summaryDraft, setSummaryDraft] = useState(job.guruSummary || "");
  const [savingSummary, setSavingSummary] = useState(false);
  const nextAfterSummaryRef = useRef("checklist");
  // parts
  const [materials, setMaterials] = useState([]);
  const [search, setSearch] = useState("");
  const [parts, setParts] = useState([]);
  const [customName, setCustomName] = useState("");
  // disposition — dropdown sourced live from the "Jobs" pipeline board columns
  const [stages, setStages] = useState([]);
  const [disposition, setDisposition] = useState("");
  useEffect(() => {
    let cancelled = false;
    pipelineApi.stages("jobs").then((s) => { if (!cancelled) setStages(s || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  // payment
  const invoice = useMemo(
    () =>
      (invoices || []).find(
        (i) =>
          i.jobId === job.id &&
          i.status !== "Voided" &&
          Number(i.balanceDue ?? Number(i.total || 0) - Number(i.amountPaid || 0)) > 0.005,
      ) || null,
    [invoices, job.id],
  );
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null); // {amount, method} | "skipped" | "none"
  // signature
  const sigRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [signedBy, setSignedBy] = useState(job.customer || "");
  const [signature, setSignature] = useState(null);
  const [closing, setClosing] = useState(false);
  // Company-configured Customer Sign-Off paragraph (Company Settings →
  // Payments). Loaded once when the modal opens so an admin's edit in
  // another tab shows up on the next work order.
  const [signoffText, setSignoffText] = useState("");
  useEffect(() => {
    let cancelled = false;
    paymentProvidersApi
      .getSignoffText()
      .then((d) => { if (!cancelled) setSignoffText(d?.workCompleteSignoffText || ""); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await flowsApi.forJob(job.id);
        if (!active) return;
        const open = (res.flows || []).filter((f) => !f.completed && (f.nodes || []).length);
        if (open.length) {
          setChecklistState("pending");
          nextAfterSummaryRef.current = "checklist";
        } else {
          setChecklistState((res.flows || []).length ? "done" : "none");
          nextAfterSummaryRef.current = "parts";
        }
      } catch {
        if (active) {
          setChecklistState("none");
          nextAfterSummaryRef.current = "parts";
        }
      }
    })();
    pricebookApi.list("material").then((m) => active && setMaterials(m || [])).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.id]);

  const continueFromSummary = async () => {
    const trimmed = summaryDraft.trim();
    if (!trimmed) return;
    setSavingSummary(true);
    try {
      await jobsApi.updateSummary(job.id, trimmed);
      setStep(nextAfterSummaryRef.current);
    } catch {
      toast.error("Could not save the Job Summary");
    } finally {
      setSavingSummary(false);
    }
  };

  const filteredMaterials = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? materials.filter((m) => (m.name || "").toLowerCase().includes(q)) : materials;
    return list.slice(0, 25);
  }, [materials, search]);

  const addPart = (item) => {
    setParts((prev) => {
      const ix = prev.findIndex((p) => (item.id && p.pricebookId === item.id) || p.name === item.name);
      if (ix >= 0) return prev.map((p, i) => (i === ix ? { ...p, qty: p.qty + 1 } : p));
      return [...prev, { name: item.name, qty: 1, pricebookId: item.id || null, unit: item.unit || "" }];
    });
  };
  const stepQty = (ix, delta) =>
    setParts((prev) => prev.map((p, i) => (i === ix ? { ...p, qty: Math.max(1, p.qty + delta) } : p)));
  const removePart = (ix) => setParts((prev) => prev.filter((_, i) => i !== ix));
  const addCustom = () => {
    const name = customName.trim();
    if (!name) return;
    addPart({ name });
    setCustomName("");
  };

  const saveSignature = () => {
    if (sigRef.current?.isEmpty()) {
      toast.error("Ask the customer to sign first");
      return;
    }
    setSignature(sigRef.current.toDataURL());
    setStep("disposition");
  };

  const closeJob = async () => {
    const dispStage = stages.find((s) => s.id === disposition);
    if (!dispStage) {
      toast.error("Select a disposition first");
      setStep("disposition");
      return;
    }
    setClosing(true);
    try {
      if (parts.length || signature) {
        await jobsApi.completeWorkOrder(job.id, {
          parts,
          signature: signature || null,
          signedBy: signature ? signedBy : "",
        });
      }
      await onCompleted({ status: dispStage.status, name: dispStage.name });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not close the job");
      setClosing(false);
    }
  };

  const stepIdx = Math.max(0, STEPS.findIndex((s) => s.key === step));

  const content = (
    <>
      {/* Header */}
      <div className="bg-[#0B1E3F] text-white px-4 pt-4 pb-3 shrink-0">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-blue-300">Complete Work Order</div>
            <div className="text-[16px] font-extrabold truncate">
              {job.id} — {job.customer}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="wo-close"
            className="h-9 w-9 rounded-xl bg-white/10 flex items-center justify-center active:bg-white/20 shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex items-center gap-1.5 mt-3.5" data-testid="wo-progress">
          {STEPS.map((s, i) => (
            <div key={s.key} className="flex-1 flex flex-col items-center gap-1">
              <div
                className={`h-1.5 w-full rounded-full ${
                  i < stepIdx ? "bg-emerald-400" : i === stepIdx ? "bg-blue-400" : "bg-white/15"
                }`}
              />
              <span
                className={`text-[9.5px] font-bold uppercase tracking-wide ${
                  i === stepIdx ? "text-white" : i < stepIdx ? "text-emerald-300" : "text-white/40"
                }`}
              >
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {step === "loading" && (
          <div className="flex justify-center pt-16">
            <Loader2 className="h-7 w-7 text-blue-500 animate-spin" />
          </div>
        )}

        {step === "jobsummary" && (
          <>
            <StepCard title="Job Summary" subtitle="Summarize this visit — required before you can continue.">
              <textarea
                value={summaryDraft}
                onChange={(e) => setSummaryDraft(e.target.value)}
                rows={5}
                placeholder="What did you do on this visit?"
                data-testid="wo-jobsummary-input"
                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[14px] leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500/30 resize-y"
              />
              {!summaryDraft.trim() && (
                <div className="mt-2 text-[12px] text-rose-500" data-testid="wo-jobsummary-required">
                  A job summary is required to continue.
                </div>
              )}
            </StepCard>
            <button
              onClick={continueFromSummary}
              disabled={!summaryDraft.trim() || savingSummary}
              data-testid="wo-jobsummary-continue"
              className="w-full h-12 rounded-xl bg-[#2563EB] text-white text-[14px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              {savingSummary && <Loader2 className="h-4 w-4 animate-spin" />} Continue
            </button>
          </>
        )}

        {step === "checklist" && (
          <>
            <StepCard title="Required checklist" subtitle="Finish every required checklist item to continue.">
              <div className="flex items-center gap-2.5 text-[13.5px] text-slate-500">
                <ClipboardCheck className="h-5 w-5 text-blue-500" /> Checklist in progress…
              </div>
            </StepCard>
            <JobCompletionFlow
              job={job}
              skipConfirm
              onClose={onClose}
              onCompleted={() => {
                setChecklistState("done");
                setStep("parts");
              }}
            />
          </>
        )}

        {step === "parts" && (
          <>
            <StepCard title="Parts installed" subtitle="Record any parts used on this job — the office will review them.">
              {parts.length > 0 ? (
                <div className="space-y-2 mb-3" data-testid="wo-selected-parts">
                  {parts.map((p, ix) => (
                    <div key={`${p.name}-${ix}`} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2">
                      <span className="flex-1 text-[13.5px] font-semibold text-[#0B1E3F] truncate">{p.name}</span>
                      <button
                        onClick={() => stepQty(ix, -1)}
                        data-testid={`wo-part-minus-${ix}`}
                        className="h-8 w-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center"
                      >
                        <Minus className="h-3.5 w-3.5 text-slate-600" />
                      </button>
                      <span className="w-7 text-center text-[14px] font-extrabold text-[#0B1E3F]" data-testid={`wo-part-qty-${ix}`}>
                        {p.qty}
                      </span>
                      <button
                        onClick={() => stepQty(ix, 1)}
                        data-testid={`wo-part-plus-${ix}`}
                        className="h-8 w-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center"
                      >
                        <Plus className="h-3.5 w-3.5 text-slate-600" />
                      </button>
                      <button
                        onClick={() => removePart(ix)}
                        data-testid={`wo-part-remove-${ix}`}
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-rose-500"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[12.5px] text-slate-400 mb-3">No parts added yet.</div>
              )}
              <div className="relative">
                <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search pricebook materials…"
                  data-testid="wo-parts-search"
                  className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-white text-[14px] text-[#0B1E3F] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
              </div>
              {filteredMaterials.length > 0 && (
                <div className="mt-2 max-h-52 overflow-y-auto divide-y divide-slate-100 rounded-xl border border-slate-100 bg-white">
                  {filteredMaterials.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => addPart(m)}
                      data-testid={`wo-material-${m.id}`}
                      className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left active:bg-blue-50"
                    >
                      <span className="text-[13px] font-medium text-[#0B1E3F] truncate">{m.name}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        {m.price != null && <span className="text-[11.5px] text-slate-400">{money(m.price)}</span>}
                        <Plus className="h-4 w-4 text-blue-600" />
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <input
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addCustom()}
                  placeholder="Custom part name"
                  data-testid="wo-custom-part-input"
                  className="flex-1 h-11 px-3 rounded-xl border border-slate-200 bg-white text-[14px] text-[#0B1E3F] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                />
                <button
                  onClick={addCustom}
                  data-testid="wo-custom-part-add"
                  className="h-11 px-4 rounded-xl bg-slate-800 text-white text-[13px] font-bold"
                >
                  Add
                </button>
              </div>
            </StepCard>
            <button
              onClick={() => setStep("payment")}
              data-testid="wo-parts-continue"
              className="w-full h-12 rounded-xl bg-[#2563EB] text-white text-[14px] font-bold active:scale-[0.98] transition-transform"
            >
              {parts.length ? `Continue — ${parts.length} part${parts.length > 1 ? "s" : ""} recorded` : "Continue — no parts used"}
            </button>
          </>
        )}

        {step === "payment" && (
          <>
            <BackLink onClick={() => setStep("parts")} testid="wo-back-parts" />
            {invoice ? (
              <StepCard title="Collect payment" subtitle={`Invoice ${invoice.id}`}>
                <div className="flex items-center justify-between py-1.5">
                  <span className="text-[12.5px] text-slate-500">Total</span>
                  <span className="text-[14px] font-bold text-[#0B1E3F]">{money(invoice.total)}</span>
                </div>
                <div className="flex items-center justify-between py-1.5 border-b border-slate-100 mb-3">
                  <span className="text-[12.5px] text-slate-500">Balance due</span>
                  <span className="text-[16px] font-extrabold text-amber-600" data-testid="wo-balance-due">
                    {money(invoice.balanceDue ?? invoice.total)}
                  </span>
                </div>
                {paymentResult && paymentResult.amount ? (
                  <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-[13px] font-semibold text-emerald-700" data-testid="wo-pay-done">
                    <CheckCircle2 className="h-4 w-4" /> {money(paymentResult.amount)} accepted by {paymentResult.method}
                  </div>
                ) : (
                  <button
                    onClick={() => setPayModalOpen(true)}
                    data-testid="wo-pay-process"
                    className="w-full h-12 rounded-xl bg-emerald-600 text-white text-[14px] font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                  >
                    <DollarSign className="h-4 w-4" /> Process Payment
                  </button>
                )}
              </StepCard>
            ) : (
              <StepCard title="Collect payment">
                <div className="flex items-center gap-2.5 text-[13.5px] text-slate-500" data-testid="wo-no-invoice">
                  <FileText className="h-5 w-5 text-slate-400 shrink-0" /> No open invoice on this job — nothing to collect.
                </div>
              </StepCard>
            )}
            <button
              onClick={() => {
                if (!paymentResult) setPaymentResult(invoice ? "skipped" : "none");
                setStep("signature");
              }}
              data-testid="wo-pay-skip"
              className="w-full h-11 rounded-xl border border-slate-300 bg-white text-slate-600 text-[13.5px] font-semibold"
            >
              {paymentResult && paymentResult.amount ? "Continue" : invoice ? "Skip — collect later" : "Continue"}
            </button>
            {payModalOpen && invoice && (
              <ProcessPaymentModal
                kind="invoice"
                docId={invoice.id}
                docLabel={`Invoice ${invoice.id}`}
                customerName={job.customer}
                balance={Number(invoice.balanceDue ?? invoice.total ?? 0)}
                onClose={() => setPayModalOpen(false)}
                onDone={(updated, info) => {
                  setPayModalOpen(false);
                  setPaymentResult(info);
                  setStep("signature");
                }}
              />
            )}
          </>
        )}

        {step === "signature" && (
          <>
            <BackLink onClick={() => setStep("payment")} testid="wo-back-payment" />
            <StepCard title="Customer sign-off" subtitle="Have the customer sign to confirm the work is complete.">
              {signoffText && (
                <div
                  data-testid="wo-signoff-paragraph"
                  className="mb-3 rounded-xl bg-white border border-slate-200 px-3 py-2.5 text-[12.5px] leading-relaxed text-black whitespace-pre-wrap"
                >
                  {signoffText}
                </div>
              )}
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Signed by</div>
              <input
                value={signedBy}
                onChange={(e) => setSignedBy(e.target.value)}
                data-testid="wo-signature-name"
                className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-[14px] text-[#0B1E3F] focus:outline-none focus:ring-2 focus:ring-blue-500/30 mb-3"
              />
              <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <SignaturePad ref={sigRef} onChange={setHasInk} height={190} />
              </div>
            </StepCard>
            <button
              onClick={saveSignature}
              disabled={!hasInk}
              data-testid="wo-signature-continue"
              className="w-full h-12 rounded-xl bg-[#2563EB] text-white text-[14px] font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              Save signature & continue
            </button>
            <button
              onClick={() => {
                setSignature(null);
                setStep("disposition");
              }}
              data-testid="wo-signature-skip"
              className="w-full h-11 rounded-xl border border-slate-300 bg-white text-slate-600 text-[13.5px] font-semibold"
            >
              Skip — customer unavailable
            </button>
          </>
        )}

        {step === "disposition" && (
          <>
            <BackLink onClick={() => setStep("signature")} testid="wo-back-signature2" />
            <StepCard title="Disposition" subtitle="Choose how this job should be closed out.">
              <select
                value={disposition}
                onChange={(e) => setDisposition(e.target.value)}
                data-testid="wo-disposition-select"
                className="w-full h-11 px-3 rounded-xl border border-slate-200 bg-white text-[14px] text-[#0B1E3F] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                <option value="">Select a disposition…</option>
                {stages
                  .slice()
                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                  .map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
              </select>
              {!disposition && (
                <div className="mt-2 text-[12px] text-rose-500" data-testid="wo-disposition-required">
                  A disposition is required to continue.
                </div>
              )}
            </StepCard>
            <button
              onClick={() => setStep("summary")}
              disabled={!disposition}
              data-testid="wo-disposition-continue"
              className="w-full h-12 rounded-xl bg-[#2563EB] text-white text-[14px] font-bold active:scale-[0.98] transition-transform disabled:opacity-50"
            >
              Continue
            </button>
          </>
        )}

        {step === "summary" && (
          <>
            <BackLink onClick={() => setStep("disposition")} testid="wo-back-signature" />
            <StepCard title="Work order summary" subtitle="Review before closing the job.">
              <SummaryRow
                ok={checklistState !== "pending"}
                label="Checklist"
                value={checklistState === "done" ? "Completed" : "No checklist required"}
                testid="wo-summary-checklist"
              />
              <SummaryRow
                ok={parts.length > 0}
                label="Parts installed"
                value={parts.length ? parts.map((p) => `${p.qty}× ${p.name}`).join(", ") : "None recorded"}
                testid="wo-summary-parts"
              />
              <SummaryRow
                ok={!!(paymentResult && paymentResult.amount)}
                label="Payment"
                value={
                  paymentResult && paymentResult.amount
                    ? `${money(paymentResult.amount)} by ${paymentResult.method}`
                    : paymentResult === "none"
                      ? "No open invoice"
                      : "Skipped — collect later"
                }
                testid="wo-summary-payment"
              />
              <SummaryRow
                ok={!!signature}
                label="Signature"
                value={signature ? `Signed by ${signedBy || "customer"}` : "Skipped"}
                testid="wo-summary-signature"
              />
              {signature && (
                <img src={signature} alt="Customer signature" className="mt-2 h-16 bg-white rounded-lg border border-slate-200 p-1" />
              )}
              <SummaryRow
                ok={!!disposition}
                label="Disposition"
                value={stages.find((s) => s.id === disposition)?.name || "Not selected"}
                testid="wo-summary-disposition"
              />
            </StepCard>
            <button
              onClick={closeJob}
              disabled={closing || !disposition}
              data-testid="wo-close-job"
              className="w-full h-12 rounded-xl bg-emerald-600 text-white text-[15px] font-extrabold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60"
            >
              {closing ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />} Close Job
            </button>
          </>
        )}
      </div>
    </>
  );

  return desktop ? (
    <div className="fixed inset-0 z-[100] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4" data-testid="wo-complete-screen">
      <div className="w-full max-w-lg max-h-[92vh] rounded-2xl bg-slate-100 flex flex-col overflow-hidden shadow-2xl">
        {content}
      </div>
    </div>
  ) : (
    <div className="fixed inset-0 z-[100] bg-slate-100 flex flex-col" data-testid="wo-complete-screen">
      {content}
    </div>
  );
}

function StepCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm border border-slate-200">
      <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">{title}</div>
      {subtitle && <div className="text-[12px] text-slate-400 mt-0.5 mb-3">{subtitle}</div>}
      {!subtitle && <div className="mb-3" />}
      {children}
    </div>
  );
}

function BackLink({ onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-slate-500 active:text-slate-700"
    >
      <ChevronLeft className="h-4 w-4" /> Back
    </button>
  );
}

function SummaryRow({ ok, label, value, testid }) {
  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-slate-100 last:border-0" data-testid={testid}>
      {ok ? (
        <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 fill-emerald-500/15" />
      ) : (
        <span className="h-5 w-5 rounded-full border-2 border-slate-300 shrink-0" />
      )}
      <div className="min-w-0">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
        <div className="text-[13.5px] font-semibold text-[#0B1E3F] leading-snug">{value}</div>
      </div>
    </div>
  );
}
